import React, { Component } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash/get'
import { FormattedMessage, defineMessages, intlShape } from 'react-intl'
import {
  LearnMore,
  ScrolledIntoView,
  TuneNavigateBack,
  InstantOfferNavigateBack,
  EVENTS_LABELS,
  HOME_FACTS_TRACKING_PROGRESS
} from 'src/constants/trackingEvents'
import { ABBREVIATED_MONTH_WITH_YEAR } from 'src/constants/formats'
import { AVM_SOURCE_TYPES } from 'src/constants/valuation'
import { Waypoint } from 'react-waypoint'
import Panel from 'src/Components/shared/Panel'
import { BorderedTag, Button, If } from 'src/Components'
import { HeightFade } from 'src/Components/shared/Transition'
import AdditionalInfo from 'src/Components/shared/AdditionalInfo'
import { IconDollar, IconWrench } from 'src/Components/shared/Icon'
import { formatCurrency } from 'src/helpers/formatters'
import Chart from './Chart'
import AppreciationSentence from './AppreciationSentence'
import CMASection from './CMASection'
import NetWorthSection from './NetWorthSection'
import ImportantNow from './ImportantNow'
import Text from 'src/Components/shared/Text'
import {
  oneTimeTagEventDispatcher,
  dispatchEvent
} from 'src/helpers/tagManager'
import EstimatedValueZeroState from './EstimatedValueZeroState'
import GemContainer from 'src/Components/shared/Gem/GemContainer'
import RefiOptionCard from 'src/Components/shared/RefiOption/RefiOptionCard'
import GemChip from 'src/Components/shared/Gem/GemChip'
import Blur from 'src/Components/shared/Blur'
import OverlayPanel from 'src/Components/shared/Routing/OverlayPanel'
import { PATH_HELPERS, OVERLAY_HELPERS } from 'src/constants/navigation'
import history from 'src/history'
import ValuationPanelManager from 'src/Components/homeowners/Valuation'
import InstantOffer from './InstantOffer'
import {
  AVMDiffWarning,
  LowConfidenceWarning,
  NonDisclosureStateWarning
} from 'src/Components/shared/EstimatedValue/Warnings'
import OverlayDefaultHeader from 'src/Components/shared/Routing/Headers/OverlayDefaultHeader'

import styles from './EstimatedValue.module.scss'

const propTypes = {
  clientId: PropTypes.string,
  cmaDate: PropTypes.object, // TODO: moment prop types?
  customerFirstName: PropTypes.string,
  customerIsREA: PropTypes.bool,
  customerProfile: PropTypes.object,
  disableValue: PropTypes.bool,
  hasCMA: PropTypes.bool,
  homeFactsProgress: PropTypes.string,
  homeId: PropTypes.string,
  homeValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  instantOfferEnabled: PropTypes.bool,
  intl: intlShape,
  latestTuneSource: PropTypes.string,
  locked: PropTypes.bool,
  redrawHeightFade: PropTypes.func,
  showAVMDiffWarning: PropTypes.bool,
  showLowConfidenceWarning: PropTypes.bool,
  showNonDisclosureStateWarning: PropTypes.bool,
  withCurrentHomeData: PropTypes.bool
}

const infoToggleId = 'AdditionalInfo_HF'

const scrollIntoView = oneTimeTagEventDispatcher(ScrolledIntoView, {
  eventLabel: EVENTS_LABELS.EstimatedValueModule
})

const messages = defineMessages({
  you: {
    id: 'EstimatedValue.tune.you',
    defaultMessage: 'you'
  }
})

class EstimatedValue extends Component {
  constructor (props) {
    super(props)

    this.state = {
      showInstantOffer: false,
      instantOfferRef: null,
      navigatedCMA: false,
      showCMA: false,
      cmaStep: null,
      cmaRef: null
    }
  }

  handleOpenTunePanel = () => {
    this.setState({ navigatedCMA: true })
    history.push(OVERLAY_HELPERS.tuneYourValue.buildPath())
  }

  toggleCMA = (showCMA = !this.state.showCMA) => {
    if (!this.props.withCurrentHomeData) {
      return
    }

    this.props.redrawHeightFade(infoToggleId)

    this.setState({
      showCMA,
      cmaStep: null,
      showInstantOffer: showCMA ? false : undefined
    })
  }

  toggleInstantOffer = (showInstantOffer = !this.state.showInstantOffer) => {
    if (!this.props.withCurrentHomeData) {
      return
    }

    this.setState({
      showCMA: showInstantOffer ? false : undefined
    })

    history.push(OVERLAY_HELPERS.instantOffer.buildPath())
  }

  handleSetCmaStep = step => () => {
    this.props.redrawHeightFade(infoToggleId, 'fast')

    this.setState({ cmaStep: step })
  }

  setCmaRef = ref => {
    this.setState({ cmaRef: ref })
  }

  setInstantOfferRef = ref => {
    this.setState({ instantOfferRef: ref })
  }

  handleWarningNavigate = () => {
    this.handleOpenTunePanel()
  }

  handleTuneOverlayExit = () => {
    const { homeFactsProgress, customerIsREA } = this.props
    const lastStage = get(
      HOME_FACTS_TRACKING_PROGRESS,
      homeFactsProgress,
      'Ruler'
    )
    const eventLabel = `Last: ${lastStage}`

    dispatchEvent(TuneNavigateBack, {
      eventLabel,
      customerType: customerIsREA ? 'REA' : 'LO'
    })
  }

  handleInstantOfferOverlayExit = () => {
    const { homeFactsProgress, customerIsREA } = this.props
    const lastStage = get(
      HOME_FACTS_TRACKING_PROGRESS,
      homeFactsProgress,
      'Video'
    )
    const eventLabel = `Last: ${lastStage}`

    dispatchEvent(InstantOfferNavigateBack, {
      eventLabel,
      customerType: customerIsREA ? 'REA' : 'LO'
    })
  }

  render () {
    const {
      homeValue,
      customerProfile,
      hasCMA,
      withCurrentHomeData,
      instantOfferEnabled,
      disableValue,
      intl,
      showAVMDiffWarning,
      showLowConfidenceWarning,
      showNonDisclosureStateWarning,
      homeId,
      clientId,
      latestTuneSource,
      customerFirstName,
      cmaDate,
      homeFactsProgress,
      locked
    } = this.props
    const { showInstantOffer, navigatedCMA, showCMA } = this.state
    const activePath =
      !!homeId &&
      !!clientId &&
      PATH_HELPERS.home.buildPath(clientId, homeId) + window.location.search

    if (!homeValue) {
      return <EstimatedValueZeroState customerProfile={customerProfile} />
    }

    return (
      <Panel data-qa='EstimatedValueCard' className={styles.container}>
        <Panel.Heading className={styles.heading}>
          <FormattedMessage
            id='Homeowner.estimatedValue.headline'
            defaultMessage='The estimated value of your {forceNoWrap}'
            values={{
              forceNoWrap: (
                <span className={styles.forceNoWrap}>
                  <FormattedMessage
                    id='Homeowner.estimatedValue.headline.force'
                    defaultMessage='home is'
                  />
                </span>
              )
            }}
          />
        </Panel.Heading>
        <section className={styles.section}>
          <div className={styles.numberRow}>
            <div className={styles.avm}>
              <Text element='div' className={styles.homeValueText}>
                <Blur amount='most' enabled={!!disableValue}>
                  {formatCurrency(homeValue)}
                </Blur>
              </Text>

              <If condition={withCurrentHomeData}>
                <span className={styles.cmaEmblem}>
                  <If condition={!hasCMA}>
                    <BorderedTag type='danger'>
                      <FormattedMessage
                        id='Homeowner.estimatedValue.notVerifiedTag.tune'
                        defaultMessage='Not tuned yet'
                      />
                    </BorderedTag>
                  </If>
                </span>
              </If>
            </div>

            <If condition={hasCMA && withCurrentHomeData}>
              <div className={styles.cmaTuned}>
                <Text color='quiet' size='s'>
                  <FormattedMessage
                    id='Homeowner.estimatedValue.cmaVerifiedMessage.tune'
                    defaultMessage='Tuned by {name} {monthYear}'
                    values={{
                      monthYear: cmaDate.format(ABBREVIATED_MONTH_WITH_YEAR),
                      name:
                        latestTuneSource === AVM_SOURCE_TYPES.CLIENT
                          ? intl.formatMessage(messages.you)
                          : customerFirstName
                    }}
                  />
                </Text>
              </div>
            </If>
          </div>

          <If condition={showAVMDiffWarning}>
            <AVMDiffWarning />
          </If>

          <Button.Group className={styles.contactButtons}>
            <If condition={withCurrentHomeData}>
              <GemContainer
                data-qa='cmaCTA'
                onClick={this.handleOpenTunePanel}
                track={LearnMore}
                trackEnabled={!navigatedCMA}
                trackOptions={{ eventLabel: 'EstValue-OpenTYV' }}
              >
                <RefiOptionCard className={styles.card}>
                  <GemChip className={styles.chip}>
                    <IconWrench />
                  </GemChip>
                  <Text color='blue' size='s'>
                    <FormattedMessage
                      id='Homeowner.estimatedValue.tuneValueButton'
                      defaultMessage='Tune value'
                    />
                  </Text>
                </RefiOptionCard>
              </GemContainer>
            </If>
            <If condition={instantOfferEnabled}>
              <GemContainer
                data-qa='instantOfferCTA'
                onClick={() => this.toggleInstantOffer()}
                track={LearnMore}
                trackEnabled={!showInstantOffer}
                trackOptions={{ eventLabel: 'EstValue-OpenInstantOffer' }}
                ref={this.setInstantOfferRef}
              >
                <RefiOptionCard className={styles.card}>
                  <GemChip className={styles.chip}>
                    <Text color='green' element='span'>
                      <IconDollar />
                    </Text>
                  </GemChip>
                  <Text color='blue' size='s'>
                    <FormattedMessage
                      id='Homeowner.instantOffer.cta'
                      defaultMessage='Instant Offer'
                    />
                  </Text>
                </RefiOptionCard>
              </GemContainer>
            </If>
          </Button.Group>

          <OverlayPanel
            className={styles.valueOverlay}
            back={OVERLAY_HELPERS.instantOffer.getOrigin()}
            path={OVERLAY_HELPERS.instantOffer.buildPath()}
            title={OVERLAY_HELPERS.getTitle('instantOffer', intl)}
            anchorOffset={250}
            onClose={this.handleInstantOfferOverlayExit}
            component={InstantOffer}
            renderHeader={props => (
              <OverlayDefaultHeader {...props} progress={homeFactsProgress} />
            )}
          />
          <OverlayPanel
            className={styles.valueOverlay}
            back={OVERLAY_HELPERS.tuneYourValue.getOrigin()}
            path={OVERLAY_HELPERS.tuneYourValue.buildPath()}
            title={OVERLAY_HELPERS.getTitle('tuneYourValue', intl)}
            anchorOffset={250}
            onClose={this.handleTuneOverlayExit}
            component={ValuationPanelManager}
            renderHeader={props => (
              <OverlayDefaultHeader {...props} progress={homeFactsProgress} />
            )}
          />

          <If condition={withCurrentHomeData}>
            <AdditionalInfo
              visible={showCMA}
              cardWidth
              className={styles.additionalInfo}
              showDisclaimers={false}
              targetRef={showCMA ? this.state.cmaRef : null}
            >
              <HeightFade
                id={infoToggleId}
                speed={this.state.cmaStep && showCMA ? 'fast' : 'medium'}
              >
                <If condition={showCMA}>
                  <CMASection
                    onStepChange={this.handleSetCmaStep}
                    close={() => this.toggleCMA()}
                  />
                </If>
                <If condition={!showCMA}>
                  <span />
                </If>
              </HeightFade>
            </AdditionalInfo>
          </If>

          <Chart disableValue={!!disableValue} clipPath={activePath} />

          <AppreciationSentence className={styles.appreciationText} />

          <If condition={showNonDisclosureStateWarning}>
            <NonDisclosureStateWarning
              onNavigate={this.handleWarningNavigate}
            />
          </If>

          <If condition={showLowConfidenceWarning}>
            <LowConfidenceWarning onNavigate={this.handleWarningNavigate} />
          </If>
        </section>

        <ImportantNow locked={locked} />

        <NetWorthSection locked={locked} />
        <Waypoint onEnter={scrollIntoView} bottomOffset='25%'>
          <div />
        </Waypoint>
      </Panel>
    )
  }
}

EstimatedValue.propTypes = propTypes
export default EstimatedValue
